// /reports — compliance report download page (T086).
//
// Downloads are served by GET /api/v1/portfolios/{id}/reports/{type}?format=csv|pdf
// (backends: T066–T068). The page fetches the portfolio list, lets the user
// select one, and provides CSV + PDF download buttons for each report type.
//
// Because the download endpoints require auth headers, the download is
// performed via an authenticated fetch + Blob URL rather than a plain
// <a href> navigation (see `downloadReport` in lib/api.ts).

import { useState } from 'react';
import { Link } from 'react-router-dom';
import useSWR from 'swr';
import {
  Download,
  FileText,
  Layers,
  Loader2,
  Shield,
  ShieldAlert,
} from 'lucide-react';
import {
  ApiError,
  Portfolio,
  downloadReport,
  fetcher,
} from '../lib/api';
import { PageHeader } from '../components/ui';

const PORTFOLIOS_URL = '/api/v1/portfolios';

interface ReportSpec {
  id: string;
  title: string;
  subtitle: string;
  description: string;
  endpoint: string;
  csvFilename: (key: string, today: string) => string;
  pdfFilename: (key: string, today: string) => string;
  Icon: React.ComponentType<{ className?: string }>;
}

const REPORTS: ReportSpec[] = [
  {
    id: 'owasp',
    title: 'OWASP Top 10 2021',
    subtitle: 'Web application security risks',
    description:
      'Issue counts broken down by the 10 OWASP categories (A01–A10:2021), grouped by severity.',
    endpoint: 'owasp-top10',
    csvFilename: (key, today) => `owasp-top10-${key}-${today}.csv`,
    pdfFilename: (key, today) => `owasp-top10-${key}-${today}.pdf`,
    Icon: Shield,
  },
  {
    id: 'cwe',
    title: 'CWE Top 25 2023',
    subtitle: 'Most dangerous software weaknesses',
    description:
      'Issue counts broken down by the 25 CWE entries in canonical rank order, grouped by severity.',
    endpoint: 'cwe-top25',
    csvFilename: (key, today) => `cwe-top25-${key}-${today}.csv`,
    pdfFilename: (key, today) => `cwe-top25-${key}-${today}.pdf`,
    Icon: ShieldAlert,
  },
  {
    id: 'iso27001',
    title: 'ISO 27001:2022',
    subtitle: 'Annex A control mapping',
    description:
      'Issue counts mapped to ISO 27001:2022 Annex A security controls via CWE cross-reference.',
    endpoint: 'iso27001',
    csvFilename: (key, today) => `iso27001-${key}-${today}.csv`,
    pdfFilename: (key, today) => `iso27001-${key}-${today}.pdf`,
    Icon: FileText,
  },
];

// ---------------------------------------------------------------------------
// Page root
// ---------------------------------------------------------------------------

export function Reports() {
  const {
    data: portfolios,
    error: portfoliosError,
    isLoading,
  } = useSWR<Portfolio[]>(PORTFOLIOS_URL, fetcher);
  const [selectedID, setSelectedID] = useState('');

  const selectedPortfolio = portfolios?.find((p) => p.id === selectedID) ?? null;

  return (
    <div className="max-w-5xl mx-auto p-8 w-full">
      <PageHeader
        eyebrow="orbital · reports"
        title="Compliance Reports"
        description={
          <>
            Download OWASP, CWE, and ISO 27001 compliance reports in CSV or PDF
            format for any bundle.
          </>
        }
        actions={
          <Link
            to="/portfolios"
            className="inline-flex items-center gap-1.5 text-xs text-fg-muted hover:text-fg transition-colors"
          >
            <Layers className="h-3.5 w-3.5" aria-hidden="true" />
            Manage Bundles
          </Link>
        }
      />

      {/* Portfolio selector */}
      <div className="orb-card p-4 mb-6">
        <label
          className="orb-section-title text-[11px] block mb-1.5"
          htmlFor="report-portfolio"
        >
          Bundle
        </label>
        {isLoading ? (
          <div className="flex items-center gap-2 text-fg-muted text-xs py-1">
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
            Loading bundles…
          </div>
        ) : null}
        {!isLoading && portfoliosError ? (
          <p className="text-xs text-danger">
            {portfoliosError instanceof ApiError
              ? `${portfoliosError.code}: ${portfoliosError.message}`
              : 'Failed to load bundles'}
          </p>
        ) : null}
        {!isLoading && !portfoliosError && (!portfolios || portfolios.length === 0) ? (
          <p className="text-xs text-fg-muted">
            No bundles yet.{' '}
            <Link to="/portfolios" className="text-accent hover:underline">
              Create one
            </Link>{' '}
            to generate reports.
          </p>
        ) : null}
        {!isLoading && !portfoliosError && portfolios && portfolios.length > 0 ? (
          <select
            id="report-portfolio"
            value={selectedID}
            onChange={(e) => setSelectedID(e.target.value)}
            className="
              w-full sm:max-w-sm rounded-md border border-border
              bg-bg-inset text-fg text-xs px-3 py-1.5
              focus:outline-none focus:ring-2 focus:ring-accent/40 focus:border-accent/50
              transition-colors
            "
          >
            <option value="">— select a bundle —</option>
            {[...portfolios]
              .sort((a, b) => a.name.localeCompare(b.name))
              .map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} ({p.key})
                </option>
              ))}
          </select>
        ) : null}
      </div>

      {/* Report cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {REPORTS.map((spec) => (
          <ReportCard key={spec.id} spec={spec} portfolio={selectedPortfolio} />
        ))}
      </div>

      {!selectedID && portfolios && portfolios.length > 0 ? (
        <p className="text-center text-xs text-fg-subtle mt-6">
          Select a bundle above to enable downloads.
        </p>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Report card
// ---------------------------------------------------------------------------

interface ReportCardProps {
  readonly spec: ReportSpec;
  readonly portfolio: Portfolio | null;
}

function ReportCard({ spec, portfolio }: ReportCardProps) {
  const [csvLoading, setCsvLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const today = new Date().toISOString().slice(0, 10).replaceAll('-', '');

  const handleDownload = async (format: 'csv' | 'pdf') => {
    if (!portfolio) return;
    const url = `/api/v1/portfolios/${encodeURIComponent(portfolio.id)}/reports/${spec.endpoint}?format=${format}`;
    const filename =
      format === 'csv'
        ? spec.csvFilename(portfolio.key, today)
        : spec.pdfFilename(portfolio.key, today);

    const setLoading = format === 'csv' ? setCsvLoading : setPdfLoading;
    setLoading(true);
    setError(null);
    try {
      await downloadReport(url, filename);
    } catch (err) {
      let errMsg: string;
      if (err instanceof ApiError) {
        errMsg = `${err.code}: ${err.message}`;
      } else if (err instanceof Error) {
        errMsg = err.message;
      } else {
        errMsg = 'Download failed';
      }
      setError(errMsg);
    } finally {
      setLoading(false);
    }
  };

  const { Icon } = spec;

  return (
    <div className="orb-card p-5 flex flex-col gap-4">
      <div className="flex items-start gap-3">
        <Icon className="h-5 w-5 shrink-0 text-accent mt-0.5" aria-hidden="true" />
        <div>
          <h2 className="font-display text-base font-semibold text-fg leading-tight">
            {spec.title}
          </h2>
          <p className="text-xs text-accent mt-0.5">{spec.subtitle}</p>
        </div>
      </div>

      <p className="text-xs text-fg-muted flex-1">{spec.description}</p>

      {error ? (
        <p role="alert" className="text-xs text-danger">
          {error}
        </p>
      ) : null}

      <div className="flex gap-2">
        <DownloadButton
          label="CSV"
          loading={csvLoading}
          disabled={!portfolio || pdfLoading}
          onClick={() => {
            void handleDownload('csv');
          }}
        />
        <DownloadButton
          label="PDF"
          loading={pdfLoading}
          disabled={!portfolio || csvLoading}
          onClick={() => {
            void handleDownload('pdf');
          }}
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Download button
// ---------------------------------------------------------------------------

interface DownloadButtonProps {
  readonly label: string;
  readonly loading: boolean;
  readonly disabled: boolean;
  readonly onClick: () => void;
}

function DownloadButton({ label, loading, disabled, onClick }: DownloadButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || loading}
      className="
        inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md
        text-xs font-semibold uppercase tracking-[0.08em]
        bg-accent text-accent-fg hover:opacity-90
        border border-accent/40 transition-opacity
        disabled:opacity-40 disabled:cursor-not-allowed
      "
    >
      {loading ? (
        <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
      ) : (
        <Download className="h-3 w-3" aria-hidden="true" />
      )}
      {loading ? 'Downloading…' : label}
    </button>
  );
}
